import { computed, type ComputedRef, ref, watch } from 'vue';
import { useI18n } from '@/core/plugins/i18n';
import { getDeviceGIcon, getLangLabel } from '@/core/utils/searchers';
import type { Option } from '@/components/forms/select/types';
import { dummyIndex } from '../utils/consts';
import type { Props, Region, SearcherIndexed } from '../types';

/**
 * Создание и управление реактивными переменными для выбора региона
 *
 * @param props - входные props компонента
 * @param activeSearcherIndexed - объект активной поисковой системы (реактивная)
 */
export const useSelectRegion = (props: Props, activeSearcherIndexed: ComputedRef<SearcherIndexed>) => {
	const i18n = useI18n();

	/**
	 * Индекс выбранного региона
	 */
	const regionIndex = ref(dummyIndex);

	if (props.modelValue.length === 1) {
		regionIndex.value = props.modelValue[0];
	}

	if (regionIndex.value === dummyIndex) {
		if (props.forFrequency) {
			// в качетсве ключа используется region.key, а не region.index
			regionIndex.value = activeSearcherIndexed.value?.regionByIndex.values().next().value?.key ?? dummyIndex;
		} else {
			regionIndex.value = activeSearcherIndexed.value?.regionByIndex.keys().next().value ?? dummyIndex;
		}
	}

	/**
	 * Коллекция опций для выбора региона
	 */
	const optionByRegionIndex = computed(() => {
		const options = new Map<number, Option>();

		activeSearcherIndexed.value.regionByIndex?.forEach((region) => {
			let regionLabel = region.name;

			// на частоту в текущей версии устройство и язык не влияют
			// обратите внимание, в качетсве ключа используется region.key, а не region.index
			if (props.forFrequency) {
				const option: Option = {
					value: region.key,
					title: regionLabel,
				};
				if (!options.has(region.key)) options.set(region.key, option);

				return;
			}

			if (region.device) {
				regionLabel += ' (' + i18n.Common['Device_' + region.device] + ')';
			}

			const langLabel = getLangLabel(activeSearcherIndexed.value.key || 0, region.lang ?? '');
			if (langLabel) regionLabel += ' / ' + langLabel;

			const option: Option = {
				value: region.index,
				title: regionLabel,
				icon: region.device ? getDeviceGIcon(region.device) : undefined,
			};

			options.set(region.index, option);
		});

		return options;
	});

	/**
	 * Выбор максимально похожего региона в новой коллекции регионов
	 */
	watch(optionByRegionIndex, (optionByRegionIndex, oldOptionByRegionIndex) => {
		if (props.onlySearcher || regionIndex.value !== undefined && optionByRegionIndex.get(regionIndex.value)) {
			return;
		}

		let newRegionIndex = optionByRegionIndex.keys().next().value as Region['index'];
		if (regionIndex.value === dummyIndex || newRegionIndex === dummyIndex) {
			regionIndex.value = newRegionIndex;

			return;
		}

		let regionName = (oldOptionByRegionIndex?.get(regionIndex.value) as Option)?.title || '';
		let regionMatchLevel = -1;
		for (const [index, option] of optionByRegionIndex.entries()) {
			const title = (option as Option).title;

			if (typeof title !== 'string' || typeof index === 'string') {
				break;
			}

			// полное совпадение
			if (title === regionName) {
				newRegionIndex = index;

				break;
			}

			const regexpDevice = new RegExp(` \\((${i18n.Common['Device_1']}|${i18n.Common['Device_2']})\\)`);
			let regionNameCompare = regionName;
			let regionMatchLevelI = 3;

			// без устройства
			if (title.indexOf(regionNameCompare) === -1) {
				regionNameCompare = regionName.replace(/^[^a-zа-я]/i, '').replace(regexpDevice, '');

				regionMatchLevelI--;
			}

			// без языка
			if (title.indexOf(regionNameCompare) === -1) {
				regionNameCompare = regionName.replace(/ \/.*/, '');

				regionMatchLevelI--;
			}

			// без устройства и без языка
			if (title.indexOf(regionNameCompare) === -1) {
				regionNameCompare = regionName.replace(/ \/.*/, '');
				regionNameCompare = regionNameCompare.replace(/^[^a-zа-я]/i, '').replace(regexpDevice, '');

				regionMatchLevelI--;
			}

			if (title.indexOf(regionNameCompare) === -1) continue;
			if (regionMatchLevelI <= regionMatchLevel) continue;

			regionMatchLevel = regionMatchLevelI;
			newRegionIndex = index;
		}

		regionIndex.value = newRegionIndex;
	});

	return {
		regionIndex,
		optionByRegionIndex,
	};
};
